/**
 * SelectDynamic -- single-select sibling of ChipMultiSelectDynamic (#1999).
 *
 * Renders a dropdown whose options come from a companion
 * LOAD_ADVERTISERS_BUTTON, and stores ONE scalar rather than an array. Beeswax
 * needs this because `report_advertiser_id` is single-valued by construction:
 * the connector verifies every returned row against exactly one id (#1990), so
 * the multi-select type would have changed the stored settings shape.
 *
 * Two behaviours the multi-select does not need:
 *   - An explicit "every advertiser" choice, because blank is a meaningful
 *     value here (the account-wide pull that predates the filter), not an
 *     unset form.
 *   - A saved value that is not in the loaded list still renders and still
 *     round-trips. On first open nothing has been loaded yet, so a saved id
 *     would otherwise vanish from the form and be saved back as blank --
 *     silently widening the pull the operator had scoped.
 */

import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import type { Advertiser } from './LoadAdvertisersButton';

/**
 * Radix Select rejects an empty-string item value, so the "no filter" choice
 * travels under a sentinel and is translated back to '' on the way out.
 */
export const ALL_VALUES_SENTINEL = '__all__';

interface Props {
  /** Internal field name; emitted as a data attribute for integration/debugging. */
  fieldName: string;
  /** Advertisers returned by the companion loader button. Empty -> empty-state. */
  options: Advertiser[];
  /** Currently-selected id, as the string the settings store. '' = no filter. */
  value: string;
  /** Fired with the next id string ('' when the no-filter choice is picked). */
  onChange: (next: string) => void;
  /** Label for the "no filter" choice, e.g. "Every advertiser on the account". */
  emptyLabel: string;
  id?: string;
  disabled?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

export function SelectDynamic({
  fieldName,
  options,
  value,
  onChange,
  emptyLabel,
  id,
  disabled,
  ...aria
}: Props) {
  const selected = (value ?? '').trim();
  const known = options.some((o) => String(o.id) === selected);

  // One list with stable keys. Built rather than rendered inline because the
  // saved-but-unloaded entry and its loaded replacement share a value: as two
  // separate JSX branches React remounts every item when the loader returns,
  // and Radix's trigger loses the selected item's text mid-swap -- the picker
  // blanks itself at the moment it finally knows the name.
  const items: { value: string; label: string }[] = [];
  if (selected !== '' && !known) {
    items.push({
      // Shown by id alone -- naming it would mean inventing a name for an
      // advertiser nothing has told us about.
      value: selected,
      label: `Advertiser ${selected} (saved -- load to see its name)`,
    });
  }
  for (const o of options) {
    items.push({
      value: String(o.id),
      label:
        o.status && o.status.toUpperCase() === 'INACTIVE'
          ? `${o.name} (${o.id}) -- inactive`
          : `${o.name} (${o.id})`,
    });
  }

  return (
    <div data-field-name={fieldName} className="space-y-1">
      <Select
        value={selected === '' ? ALL_VALUES_SENTINEL : selected}
        onValueChange={(next) =>
          onChange(next === ALL_VALUES_SENTINEL ? '' : next)
        }
        disabled={disabled}
      >
        <SelectTrigger id={id} {...aria}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUES_SENTINEL}>{emptyLabel}</SelectItem>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {options.length === 0 && (
        <Label className="text-[12px] font-normal text-muted-foreground">
          Click Load Advertisers above to choose by name.
        </Label>
      )}
    </div>
  );
}
